import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, AlertTriangle, Calendar, Gauge, Euro, ChevronRight,
  RefreshCw, TrendingUp, Clock, FileText, ArrowRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  ALERT_PRIORITY_BADGE_COLORS,
  ALERT_TYPE_LABELS,
  formatMileage,
  formatCurrency,
  formatDate,
  getDaysUntilLabel,
} from "../../lib/fleet-constants";
import VehiclesCostsTable from "../../components/fleet/VehiclesCostsTable";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function FleetDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem("token");

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fleet/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      toast.error("Erreur lors du chargement du tableau de bord");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const criticalAlerts = dashboard?.alerts?.filter(a => a.priority === "critical") || [];
  const highAlerts = dashboard?.alerts?.filter(a => a.priority === "high") || [];
  const otherAlerts = dashboard?.alerts?.filter(a => !["critical", "high"].includes(a.priority)) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Tableau de bord Parc Auto</h1>
              <p className="text-sm text-slate-500">Vue d'ensemble de votre flotte</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button size="sm" onClick={() => navigate("/fleet/vehicles")}>
              <Car className="w-4 h-4 mr-2" />
              Voir les véhicules
            </Button>
          </div>
        </div>
      </div>
      
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Total véhicules</p>
                  <p className="text-3xl font-bold text-blue-900">{dashboard?.total_vehicles || 0}</p>
                  <p className="text-xs text-blue-600 mt-1">{dashboard?.active_vehicles || 0} actifs</p>
                </div>
                <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Car className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${dashboard?.returning_soon_count > 0 ? "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300" : "bg-white"}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${dashboard?.returning_soon_count > 0 ? "text-orange-600" : "text-slate-600"}`}>
                    À restituer (90j)
                  </p>
                  <p className={`text-3xl font-bold ${dashboard?.returning_soon_count > 0 ? "text-orange-900" : "text-slate-900"}`}>
                    {dashboard?.returning_soon_count || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">véhicule(s)</p>
                </div>
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${dashboard?.returning_soon_count > 0 ? "bg-orange-500" : "bg-slate-400"}`}>
                  <Calendar className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${dashboard?.mileage_alert_count > 0 ? "bg-gradient-to-br from-red-50 to-red-100 border-red-300" : "bg-white"}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${dashboard?.mileage_alert_count > 0 ? "text-red-600" : "text-slate-600"}`}>
                    Alerte kilométrage
                  </p>
                  <p className={`text-3xl font-bold ${dashboard?.mileage_alert_count > 0 ? "text-red-900" : "text-slate-900"}`}>
                    {dashboard?.mileage_alert_count || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">dépassement prévu</p>
                </div>
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${dashboard?.mileage_alert_count > 0 ? "bg-red-500" : "bg-slate-400"}`}>
                  <Gauge className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Coût mensuel total</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {formatCurrency(dashboard?.total_monthly_cost || 0)}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">loyers TTC</p>
                </div>
                <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <Euro className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Critical Alerts Banner */}
        {criticalAlerts.length > 0 && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-red-800">{criticalAlerts.length} alerte(s) critique(s)</p>
                  <p className="text-sm text-red-600">
                    {criticalAlerts.slice(0, 2).map(a => `${a.vehicle_name}: ${a.title}`).join(" • ")}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => document.getElementById('alerts-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Voir les alertes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Coûts par véhicule (consolidé) */}
        <VehiclesCostsTable token={token} />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Alerts Section */}
          <div className="lg:col-span-2" id="alerts-section">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Alertes prioritaires
                </CardTitle>
                <Badge variant="outline">
                  {dashboard?.alerts?.length || 0} alerte(s)
                </Badge>
              </CardHeader>
              <CardContent>
                {!dashboard?.alerts?.length ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-slate-600 font-medium">Aucune alerte</p>
                    <p className="text-sm text-slate-500">Votre parc auto est en ordre</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.alerts.slice(0, 8).map((alert) => (
                      <div
                        key={alert.id}
                        className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                        onClick={() => navigate(`/fleet/vehicles/${alert.vehicle_id}`)}
                      >
                        <Badge className={ALERT_PRIORITY_BADGE_COLORS[alert.priority]}>
                          {alert.priority === "critical" ? "URGENT" : alert.priority === "high" ? "HAUTE" : "MOYEN"}
                        </Badge>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">
                            {alert.vehicle_name}
                          </p>
                          <p className="text-xs text-slate-500">{alert.registration}</p>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-700">{alert.title}</p>
                          <p className="text-xs text-slate-500">
                            {alert.days_remaining !== null && alert.days_remaining !== undefined
                              ? getDaysUntilLabel(alert.days_remaining)
                              : alert.message}
                          </p>
                          {alert.estimated_cost > 0 && (
                            <p className="text-xs text-red-600 font-medium">
                              Coût estimé: {formatCurrency(alert.estimated_cost)}
                            </p>
                          )}
                        </div>
                        
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                    
                    {dashboard.alerts.length > 8 && (
                      <Button variant="ghost" className="w-full" onClick={() => navigate("/fleet/vehicles")}>
                        Voir toutes les alertes ({dashboard.alerts.length})
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Actions */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/fleet/vehicles")}
                >
                  <Car className="w-4 h-4 mr-3 text-indigo-600" />
                  Liste des véhicules
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/fleet/contracts")}
                >
                  <FileText className="w-4 h-4 mr-3 text-blue-600" />
                  Vue contrats
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/fleet/claims")}
                >
                  <AlertTriangle className="w-4 h-4 mr-3 text-red-500" />
                  Vue sinistres
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate("/fleet/trash")}
                >
                  <Clock className="w-4 h-4 mr-3 text-slate-500" />
                  Corbeille
                </Button>
              </CardContent>
            </Card>
            
            {/* Exports */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Exports</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/fleet/export/vehicles/csv`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `parc_auto_${new Date().toISOString().split('T')[0]}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success("Export CSV téléchargé");
                    } catch (err) {
                      toast.error("Erreur lors de l'export");
                    }
                  }}
                >
                  Export CSV véhicules
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm"
                  onClick={async () => {
                    const year = new Date().getFullYear();
                    try {
                      const response = await axios.get(`${API}/fleet/export/costs/annual?year=${year}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `couts_parc_auto_${year}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success("Export coûts téléchargé");
                    } catch (err) {
                      toast.error("Erreur lors de l'export");
                    }
                  }}
                >
                  Export coûts {new Date().getFullYear()}
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Résumé des alertes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Restitutions</span>
                    <Badge variant="outline" className={dashboard?.returning_soon_count > 0 ? "bg-orange-100 text-orange-800" : ""}>
                      {dashboard?.returning_soon_count || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Dépassements km</span>
                    <Badge variant="outline" className={dashboard?.mileage_alert_count > 0 ? "bg-red-100 text-red-800" : ""}>
                      {dashboard?.mileage_alert_count || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Alertes critiques</span>
                    <Badge variant="outline" className={criticalAlerts.length > 0 ? "bg-red-100 text-red-800" : ""}>
                      {criticalAlerts.length}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
